'use client';

import logger from '@/utils/client-logger';
import type {
  PDCardCalibration,
  PDCardCalibrationAudioResource,
  PDCardCalibrationErrorCode,
  PDCardCalibrationEventCode,
  PDCardCalibrationMeasurementResult,
  PDCardCalibrationOptions,
} from '@zeelool/pd-card-calibration';
import { createMeasurementAudioScheduler } from './measurement-audio-scheduler.client';
import { loadMeasurementSdkRemoteConfig } from './sdk-gate-limits.client';
import {
  MEASUREMENT_SDK_AUDIO_RESOURCES,
  MEASUREMENT_SDK_EVENT_CODE,
  MEASUREMENT_SDK_VERSION,
} from './sdk-contract';
import type { MeasurementTimedStep } from './measurement-timeout.client';
import type { FaceMeasurementMode } from './types';

export type MeasurementSdkFailureReason =
  | 'permission'
  | 'unsupported'
  | 'recoverable'
  | 'cancelled'
  | 'integration';

const MEASUREMENT_SDK_FAILURE_REASON_BY_CODE = {
  PD_CARD_CALIBRATION_CREATE_RUNTIME_LOAD_FAILED: 'recoverable',
  PD_CARD_CALIBRATION_CREATE_CONTAINER_INVALID: 'integration',
  PD_CARD_CALIBRATION_CREATE_ENVIRONMENT_UNSUPPORTED: 'unsupported',
  PD_CARD_CALIBRATION_CREATE_OPTIONS_INVALID: 'integration',
  PD_CARD_CALIBRATION_CREATE_MODE_INVALID: 'integration',
  PD_CARD_CALIBRATION_CREATE_OPTIONS_CONFLICT: 'integration',
  PD_CARD_CALIBRATION_CREATE_CARD_ROI_INVALID: 'integration',
  PD_CARD_CALIBRATION_CREATE_AUDIO_RESOURCES_INVALID: 'integration',
  PD_CARD_CALIBRATION_CREATE_BINOCULAR_PD_REQUIRED: 'integration',
  PD_CARD_CALIBRATION_CREATE_BINOCULAR_PD_INVALID: 'integration',
  PD_CARD_CALIBRATION_CREATE_INTERNAL_FAILED: 'recoverable',
  PD_CARD_CALIBRATION_INIT_INSTANCE_DISPOSED: 'integration',
  PD_CARD_CALIBRATION_INIT_CANCELLED: 'cancelled',
  PD_CARD_CALIBRATION_INIT_FACE_WORKER_LOAD_FAILED: 'recoverable',
  PD_CARD_CALIBRATION_INIT_FACE_WORKER_TIMEOUT: 'recoverable',
  PD_CARD_CALIBRATION_INIT_FACE_WORKER_CANVAS_UNSUPPORTED: 'unsupported',
  PD_CARD_CALIBRATION_INIT_FACE_LANDMARKER_FAILED: 'recoverable',
  PD_CARD_CALIBRATION_INIT_CARD_WORKER_LOAD_FAILED: 'recoverable',
  PD_CARD_CALIBRATION_INIT_CARD_WORKER_TIMEOUT: 'recoverable',
  PD_CARD_CALIBRATION_INIT_CARD_WORKER_CANVAS_UNSUPPORTED: 'unsupported',
  PD_CARD_CALIBRATION_INIT_CARD_DETECTOR_FAILED: 'recoverable',
  PD_CARD_CALIBRATION_INIT_FAILED: 'recoverable',
  PD_CARD_CALIBRATION_START_INSTANCE_DISPOSED: 'integration',
  PD_CARD_CALIBRATION_START_NOT_INITIALIZED: 'integration',
  PD_CARD_CALIBRATION_START_CAMERA_INSECURE_CONTEXT: 'unsupported',
  PD_CARD_CALIBRATION_START_CAMERA_API_UNAVAILABLE: 'unsupported',
  PD_CARD_CALIBRATION_START_CAMERA_PERMISSION_DENIED: 'permission',
  PD_CARD_CALIBRATION_START_CAMERA_PERMISSION_TIMEOUT: 'permission',
  PD_CARD_CALIBRATION_START_CAMERA_NOT_FOUND: 'unsupported',
  PD_CARD_CALIBRATION_START_CAMERA_BUSY: 'recoverable',
  PD_CARD_CALIBRATION_START_CAMERA_NOT_READABLE: 'recoverable',
  PD_CARD_CALIBRATION_START_CAMERA_CONSTRAINT_UNSUPPORTED: 'unsupported',
  PD_CARD_CALIBRATION_START_CAMERA_DOCUMENT_INACTIVE: 'cancelled',
  PD_CARD_CALIBRATION_START_CAMERA_REQUEST_INVALID: 'integration',
  PD_CARD_CALIBRATION_START_CAMERA_PLAY_FAILED: 'recoverable',
  PD_CARD_CALIBRATION_START_CAMERA_PLAY_TIMEOUT: 'recoverable',
  PD_CARD_CALIBRATION_START_CAMERA_METADATA_TIMEOUT: 'recoverable',
  PD_CARD_CALIBRATION_START_CAMERA_FRAME_TIMEOUT: 'recoverable',
  PD_CARD_CALIBRATION_START_CAMERA_CANCELLED: 'cancelled',
  PD_CARD_CALIBRATION_START_CAMERA_FAILED: 'recoverable',
  PD_CARD_CALIBRATION_START_INTERNAL_FAILED: 'recoverable',
} as const satisfies Record<PDCardCalibrationErrorCode, MeasurementSdkFailureReason>;

const MEASUREMENT_SDK_ERROR_CODES = new Set<PDCardCalibrationErrorCode>(
  Object.keys(MEASUREMENT_SDK_FAILURE_REASON_BY_CODE) as PDCardCalibrationErrorCode[],
);

function getMeasurementSdkErrorCode(error: unknown): PDCardCalibrationErrorCode | null {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('operation' in error) ||
    !('code' in error)
  ) {
    return null;
  }
  const operation = error.operation;
  const code = error.code;
  if (
    (operation !== 'init' && operation !== 'start') ||
    typeof code !== 'string' ||
    !code.startsWith(`PD_CARD_CALIBRATION_${operation.toUpperCase()}_`) ||
    !MEASUREMENT_SDK_ERROR_CODES.has(code as PDCardCalibrationErrorCode)
  ) {
    return null;
  }
  return code as PDCardCalibrationErrorCode;
}

export function resolveMeasurementSdkFailureReasonByCode(
  code: PDCardCalibrationErrorCode,
): MeasurementSdkFailureReason {
  return MEASUREMENT_SDK_FAILURE_REASON_BY_CODE[code];
}

export type MeasurementPromptVisual =
  | 'center-face'
  | 'move-face-right'
  | 'move-face-left'
  | 'move-face-up'
  | 'move-face-down'
  | 'move-closer'
  | 'move-farther'
  | 'straighten-head'
  | 'turn-head-right'
  | 'turn-head-left'
  | 'raise-chin'
  | 'lower-chin'
  | 'place-card'
  | 'level-card'
  | 'invalid-card'
  | 'measuring';

export interface MeasurementSdkSession {
  version: string;
  init: () => Promise<void>;
  start: () => Promise<void>;
  restart: () => void;
  setMuted: (muted: boolean) => void;
  stopGuidance: () => void;
  dispose: () => void;
}

export interface MeasurementSdkCallbacks {
  onMessage: (code: PDCardCalibrationEventCode, visual: MeasurementPromptVisual) => void;
  onTimedStep: (step: MeasurementTimedStep | null) => void;
  onResult: (result: PDCardCalibrationMeasurementResult) => void;
  onProgress?: (progress: number) => void;
  onMeasurementTimeout?: () => void;
}

export interface MeasurementSdkConfig {
  mode: FaceMeasurementMode;
  binocularPdMm?: number;
}

export interface MeasurementSdkAudioConfig {
  resources: readonly PDCardCalibrationAudioResource[];
  stepEnterDelayMs: number;
  interPromptGapMs: number;
}

export const DEFAULT_MEASUREMENT_SDK_AUDIO_CONFIG: MeasurementSdkAudioConfig = {
  resources: MEASUREMENT_SDK_AUDIO_RESOURCES,
  stepEnterDelayMs: 1_000,
  interPromptGapMs: 2_000,
};

class MeasurementSdkFailureError extends Error {
  readonly name = 'MeasurementSdkFailureError';

  constructor(
    readonly reason: MeasurementSdkFailureReason,
    readonly originalError: unknown,
  ) {
    super(`Face measurement SDK failed: ${reason}`);
  }
}

const MEASUREMENT_SDK_EVENT_CODES = new Set<PDCardCalibrationEventCode>(
  Object.values(MEASUREMENT_SDK_EVENT_CODE),
);

const DEFAULT_FACE_GUIDE_INSET_RATIO = 0.08;
const FACE_GUIDE_UPWARD_OFFSET_PX = 24;

function resolveFaceGuideInsetsPx(container: HTMLElement) {
  const bounds = container.getBoundingClientRect?.();
  if (
    !bounds ||
    !Number.isFinite(bounds.width) ||
    !Number.isFinite(bounds.height) ||
    bounds.width <= 0 ||
    bounds.height <= 0
  ) {
    return undefined;
  }

  const horizontalInsetPx = bounds.width * DEFAULT_FACE_GUIDE_INSET_RATIO;
  const verticalInsetPx = bounds.height * DEFAULT_FACE_GUIDE_INSET_RATIO;

  return {
    top: Math.max(0, verticalInsetPx - FACE_GUIDE_UPWARD_OFFSET_PX),
    right: horizontalInsetPx,
    bottom: verticalInsetPx + FACE_GUIDE_UPWARD_OFFSET_PX,
    left: horizontalInsetPx,
  };
}

function validateMeasurementSdkAudioResources(
  resources: readonly PDCardCalibrationAudioResource[],
): void {
  const configuredCodes = new Set<PDCardCalibrationEventCode>();

  for (const resource of resources) {
    if (
      !MEASUREMENT_SDK_EVENT_CODES.has(resource.eventCode) ||
      configuredCodes.has(resource.eventCode) ||
      typeof resource.audioPath !== 'string' ||
      resource.audioPath.trim() === ''
    ) {
      throw new MeasurementSdkFailureError(
        'integration',
        new TypeError('Invalid or duplicate measurement SDK audio resource'),
      );
    }
    configuredCodes.add(resource.eventCode);
  }
}

export function resolveMeasurementSdkFailureReason(error: unknown): MeasurementSdkFailureReason {
  if (error instanceof MeasurementSdkFailureError) return error.reason;
  if (typeof error === 'object' && error !== null && 'reason' in error) {
    switch (error.reason) {
      case 'permission':
      case 'unsupported':
      case 'recoverable':
      case 'cancelled':
      case 'integration':
        return error.reason;
    }
  }
  return 'recoverable';
}

export async function createMeasurementSdkSession(
  container: HTMLElement,
  config: MeasurementSdkConfig,
  callbacks: MeasurementSdkCallbacks,
  audioConfig?: MeasurementSdkAudioConfig,
): Promise<MeasurementSdkSession> {
  const resolveFailureReason = (error: unknown): MeasurementSdkFailureReason => {
    const code = getMeasurementSdkErrorCode(error);
    return code ? resolveMeasurementSdkFailureReasonByCode(code) : 'recoverable';
  };

  if (
    config.mode === 'pd-input' &&
    (!Number.isFinite(config.binocularPdMm) ||
      (config.binocularPdMm as number) < 40 ||
      (config.binocularPdMm as number) > 90)
  ) {
    throw new MeasurementSdkFailureError(
      'integration',
      new RangeError('Invalid binocular PD for PD-input mode'),
    );
  }

  const overlayOptions = {
    businessUI: true,
    faceFill: false,
  } as const;
  const faceGuideInsetsPx = resolveFaceGuideInsetsPx(container);
  const audioResources = audioConfig?.resources ?? [];
  validateMeasurementSdkAudioResources(audioResources);

  // SDK、远程门控配置和技术埋点仅在用户实际进入测量流程后并行加载。
  const [sdkModule, telemetryModule, remoteConfig] = await Promise.all([
    import('@zeelool/pd-card-calibration'),
    import('./sdk-telemetry.client').catch((error: unknown) => {
      logger.error('[FaceMeasurement] Failed to load SDK telemetry bridge', error);
      return null;
    }),
    loadMeasurementSdkRemoteConfig(config.mode),
  ]);
  const options: PDCardCalibrationOptions =
    config.mode === 'pd-input'
      ? {
          mode: 'pd-input',
          binocularPdMm: config.binocularPdMm as number,
          ...(remoteConfig?.gateLimits ? { gateLimits: remoteConfig.gateLimits } : {}),
          ...(remoteConfig?.cameraDistanceMultiplier != null
            ? { cameraDistanceMultiplier: remoteConfig.cameraDistanceMultiplier }
            : {}),
          audioResources,
          faceGuideInsetsPx,
          showOverlay: true,
          overlayOptions,
        }
      : {
          mode: 'card',
          cardRoiRegion: 'forehead',
          ...(remoteConfig?.gateLimits ? { gateLimits: remoteConfig.gateLimits } : {}),
          ...(remoteConfig?.samplingPolicy ? { samplingPolicy: remoteConfig.samplingPolicy } : {}),
          ...(remoteConfig?.cameraDistanceMultiplier != null
            ? { cameraDistanceMultiplier: remoteConfig.cameraDistanceMultiplier }
            : {}),
          audioResources,
          faceGuideInsetsPx,
          showOverlay: true,
          overlayOptions,
        };
  const releaseTelemetry = telemetryModule
    ? await telemetryModule.acquireMeasurementSdkTelemetry(
        sdkModule.createPDCardCalibration.subscribeTelemetry,
      )
    : () => undefined;
  let sdk: PDCardCalibration<PDCardCalibrationOptions>;
  try {
    sdk = await sdkModule.createPDCardCalibration(container, options);
  } catch (error) {
    releaseTelemetry();
    const reason = sdkModule.createPDCardCalibration.isFailure(error)
      ? resolveMeasurementSdkFailureReasonByCode(error.code)
      : 'recoverable';
    throw new MeasurementSdkFailureError(reason, error);
  }

  const resolvePromptVisual = (code: PDCardCalibrationEventCode): MeasurementPromptVisual => {
    switch (code) {
      case MEASUREMENT_SDK_EVENT_CODE.CenterFace:
        return 'center-face';
      case MEASUREMENT_SDK_EVENT_CODE.MoveFaceRight:
        return 'move-face-right';
      case MEASUREMENT_SDK_EVENT_CODE.MoveFaceLeft:
        return 'move-face-left';
      case MEASUREMENT_SDK_EVENT_CODE.MoveFaceUp:
        return 'move-face-up';
      case MEASUREMENT_SDK_EVENT_CODE.MoveFaceDown:
        return 'move-face-down';
      case MEASUREMENT_SDK_EVENT_CODE.MoveCloser:
        return 'move-closer';
      case MEASUREMENT_SDK_EVENT_CODE.MoveFarther:
        return 'move-farther';
      case MEASUREMENT_SDK_EVENT_CODE.StraightenHead:
        return 'straighten-head';
      case MEASUREMENT_SDK_EVENT_CODE.TurnHeadRight:
        return 'turn-head-right';
      case MEASUREMENT_SDK_EVENT_CODE.TurnHeadLeft:
        return 'turn-head-left';
      case MEASUREMENT_SDK_EVENT_CODE.RaiseChin:
        return 'raise-chin';
      case MEASUREMENT_SDK_EVENT_CODE.LowerChin:
        return 'lower-chin';
      case MEASUREMENT_SDK_EVENT_CODE.CardNotDetected:
        return 'place-card';
      case MEASUREMENT_SDK_EVENT_CODE.CardNotLevel:
        return 'level-card';
      case MEASUREMENT_SDK_EVENT_CODE.CardAspectRatioInvalid:
        return 'invalid-card';
      case MEASUREMENT_SDK_EVENT_CODE.Measuring:
        return 'measuring';
    }
  };

  const playableCodes = new Set(audioResources.map((resource) => resource.eventCode));
  const preloadResults = Promise.allSettled(
    audioResources.map(({ eventCode }) => sdk.audioPreload(eventCode)),
  );
  void preloadResults.then((results) => {
    results.forEach((result, index) => {
      if (result.status !== 'rejected') return;
      logger.warn('[FaceMeasurement] Failed to preload SDK prompt audio', {
        code: audioResources[index].eventCode,
        error: result.reason,
      });
    });
  });
  const audioScheduler = audioConfig
    ? createMeasurementAudioScheduler({
        player: {
          play: (code) => sdk.audioPlay(code),
          stop: () => sdk.audioStop(),
        },
        playableCodes,
        stepEnterDelayMs: audioConfig.stepEnterDelayMs,
        interPromptGapMs: audioConfig.interPromptGapMs,
        onError: (error, code) => {
          logger.warn('[FaceMeasurement] Failed to play SDK prompt audio', { code, error });
        },
      })
    : null;
  let guidanceEnabled = true;
  let wasRunning = false;
  let disposed = false;

  sdk.messageObservable.add((event) => {
    callbacks.onMessage(event.code, resolvePromptVisual(event.code));
    callbacks.onTimedStep(
      event.code === MEASUREMENT_SDK_EVENT_CODE.CenterFace
        ? 'center-face'
        : event.code === MEASUREMENT_SDK_EVENT_CODE.CardNotDetected
          ? 'place-card'
          : event.code === MEASUREMENT_SDK_EVENT_CODE.CardAspectRatioInvalid
            ? 'invalid-card'
            : null,
    );
    if (guidanceEnabled) audioScheduler?.updatePrompt(event.code);
  });
  sdk.stateObservable.add((state) => {
    if (disposed) return;
    // v1.0.29 卡片 deadline 通过 running=false、无结果的状态终止，不触发成功事件。
    const measurementTimedOut =
      state.mode === 'card' && wasRunning && !state.running && !state.finalResult;
    wasRunning = state.running;
    if (measurementTimedOut) {
      guidanceEnabled = false;
      audioScheduler?.reset();
      callbacks.onMeasurementTimeout?.();
      return;
    }
    callbacks.onProgress?.(state.progress);
  });
  sdk.measurementCompleteObservable.add((result) => {
    callbacks.onResult(result);
  });

  return {
    version: MEASUREMENT_SDK_VERSION,
    init: async () => {
      try {
        await sdk.init();
      } catch (error) {
        throw new MeasurementSdkFailureError(resolveFailureReason(error), error);
      }
    },
    start: async () => {
      try {
        await sdk.start();
      } catch (error) {
        throw new MeasurementSdkFailureError(resolveFailureReason(error), error);
      }
    },
    restart: () => {
      wasRunning = false;
      guidanceEnabled = true;
      audioScheduler?.reset();
      sdk.restart();
    },
    setMuted: (muted) => {
      audioScheduler?.setMuted(muted);
    },
    stopGuidance: () => {
      guidanceEnabled = false;
      audioScheduler?.reset();
    },
    dispose: () => {
      disposed = true;
      guidanceEnabled = false;
      audioScheduler?.dispose();
      try {
        sdk.dispose();
      } finally {
        // SDK 会在 dispose 中结算仍在进行的 telemetry，之后再释放包级订阅。
        releaseTelemetry();
      }
    },
  };
}
